import React, { useEffect, useState } from 'react';
import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import { purchaseService } from '../services/purchaseService';
import { useBillingStore } from '../stores/billingStore';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from './LoadingIndicator';

const PaywallModal = () => {
  const { paywall, closePaywall, openPaywall } = useBillingStore((s) => s);
  const { registerHandler } = useUnifiedWebSocket(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const refreshBillingStatusSoon = () => {
    window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
    }, 1500);
  };

  useEffect(() => {
    const onInsufficient = (evt) => {
      const detail = evt?.detail || {};
      openPaywall({ ...detail, source: detail.source || 'http' });
    };
    window.addEventListener('billing:insufficient_tokens', onInsufficient);
    return () => window.removeEventListener('billing:insufficient_tokens', onInsufficient);
  }, [openPaywall]);

  useEffect(() => {
    const off = registerHandler('insufficient_tokens', (data) => {
      openPaywall({ ...data, source: 'ws' });
    }, null);
    return () => {
      if (typeof off === 'function') off();
    };
  }, [openPaywall, registerHandler]);

  const buyTokens = async () => {
    setIsLoading(true);
    try {
      const result = await purchaseService.purchaseTokens({
        packId: 'topup_100',
        userId: user?.id,
      });

      if (result?.channel === 'revenuecat') {
        closePaywall();
        toast.success('Purchase completed. Your token balance will refresh shortly.');
        refreshBillingStatusSoon();
      }
    } catch (error) {
      if (error?.userCancelled) return;
      console.error('Token purchase failed:', error);
      toast.error(error?.message || 'Failed to start checkout.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    closePaywall();
    const target = paywall?.returnTo;
    if (target && location.pathname !== target) {
      navigate(target, { replace: true });
    }
  };

  if (!paywall?.isOpen) return null;

  const isGated = paywall.isGated;
  // Proactive route gates use onboarding-style copy; reactive 402s are mid-session depletion.
  const isFirstTimeWall = isGated && paywall.tokensRequired === null;

  return (
    <div className="fixed inset-0 z-[70000] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl bg-[#111210] border border-white/10 shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-0">
          {isGated ? (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-6"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Go back
            </button>
          ) : (
            <div />
          )}
          {!isGated && (
            <button
              type="button"
              onClick={closePaywall}
              className="ml-auto rounded-full p-1.5 text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-5 pt-4 pb-6">
          {/* Icon */}
          <div className="mb-6">
            <div className="scale-[1.35] origin-left transform-gpu">
              <LoadingIndicator className="!mt-0 !py-0 !px-0" />
            </div>
          </div>

          {/* Headline */}
          <h2 className="text-xl font-bold text-white mb-3">
            {isFirstTimeWall ? 'Ready to explore with Okio?' : 'You\'re out of tokens'}
          </h2>
          <p className="text-sm text-gray-400 mb-5 leading-relaxed">
            {isFirstTimeWall
              ? 'Get your first token pack to start identifying plants, fungi, and wildlife — guided step by step.'
              : 'Top up to keep going. Your exploration is waiting.'}
          </p>

          {/* Token debug info — only when actually ran out mid-session */}
          {!isFirstTimeWall && paywall.tokensRequired !== null && (
            <div className="mb-4 rounded-xl bg-white/[0.04] border border-white/8 px-4 py-3 text-xs text-gray-400 flex justify-between">
              <span>Balance: <span className="text-white font-medium">{paywall.tokensRemaining ?? 0}</span></span>
              <span>Needed: <span className="text-white font-medium">{paywall.tokensRequired}</span></span>
            </div>
          )}

          {/* Primary CTA */}
          <button
            type="button"
            disabled={isLoading}
            onClick={buyTokens}
            className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 px-4 py-3.5 text-sm font-semibold text-white transition-colors shadow-lg shadow-emerald-500/20 mb-2"
          >
            {isLoading ? 'Opening checkout…' : 'Get 100 tokens · $5'}
          </button>

          <p className="mt-3 text-center text-xs text-gray-600">
            Secure checkout via Stripe · No subscription required
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
